import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import enLang from '../../assets/i18n/en.json';

export type LanguageCode = 'en' | 'hi' | 'gu' | 'mr' | 'pa' | 'ta' | 'te' | 'bn';

interface TranslationDictionary {
  [key: string]: string;
}

const DYNAMIC_IMPORTERS: Record<LanguageCode, () => Promise<{ default: TranslationDictionary } | TranslationDictionary>> = {
  en: () => Promise.resolve({ default: enLang as TranslationDictionary }),
  hi: () => import('../../assets/i18n/hi.json'),
  gu: () => import('../../assets/i18n/gu.json'),
  mr: () => import('../../assets/i18n/mr.json'),
  pa: () => import('../../assets/i18n/pa.json'),
  ta: () => import('../../assets/i18n/ta.json'),
  te: () => import('../../assets/i18n/te.json'),
  bn: () => import('../../assets/i18n/bn.json')
};

@Injectable({
  providedIn: 'root'
})
export class HelperTranslationControllerService {
  private translations: Partial<Record<LanguageCode, TranslationDictionary>> = {
    en: enLang as TranslationDictionary
  };

  private currentLangSubject = new BehaviorSubject<LanguageCode>(this.getInitialLanguage());
  public currentLang$: Observable<LanguageCode> = this.currentLangSubject.asObservable();

  constructor() {
    const initial = this.getInitialLanguage();
    if (initial !== 'en') {
      this.loadLanguageAsync(initial);
    }
  }

  private getInitialLanguage(): LanguageCode {
    const saved = (typeof localStorage !== 'undefined' ? localStorage.getItem('app_language') : null) as LanguageCode;
    const validCodes: LanguageCode[] = ['en', 'hi', 'gu', 'mr', 'pa', 'ta', 'te', 'bn'];
    return saved && validCodes.includes(saved) ? saved : 'en';
  }

  public get currentLanguage(): LanguageCode {
    return this.currentLangSubject?.value || 'en';
  }

  public async loadLanguageAsync(code: LanguageCode): Promise<void> {
    if (this.translations[code]) return;
    if (DYNAMIC_IMPORTERS[code]) {
      try {
        const module = await DYNAMIC_IMPORTERS[code]();
        this.translations[code] = ((module as any).default || module) as TranslationDictionary;
      } catch (err) {
        console.warn(`Failed to dynamically load translations for ${code}:`, err);
      }
    }
  }

  public setLanguage(code: LanguageCode): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('app_language', code);
    }
    this.currentLangSubject.next(code);

    if (!this.translations[code]) {
      this.loadLanguageAsync(code).then(() => {
        this.currentLangSubject.next(code);
      });
    }
  }

  public translate(key: string, langOverride?: LanguageCode): string {
    if (!key) return '';
    const lang = (langOverride && this.translations?.[langOverride]) ? langOverride : this.currentLanguage;
    const currentDict = this.translations?.[lang];
    const fallbackDict = this.translations?.['en'];

    if (currentDict && currentDict[key]) {
      return currentDict[key];
    }
    if (fallbackDict && fallbackDict[key]) {
      return fallbackDict[key];
    }
    return key;
  }
}
